// pages/Login.js
import { useState } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { collection, query, where, getDocs, doc, getDoc, limit, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { Link, useNavigate } from 'react-router-dom';
import { FcGoogle } from 'react-icons/fc';

function Login() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // Novo estado para mostrar/ocultar senha
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Função para verificar se o usuário é admin
  const checkUserIsAdmin = async (userId) => {
    try {
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.isAdmin === true;
      }
      return false;
    } catch (error) {
      console.error('Erro ao verificar admin:', error);
      return false;
    }
  };

  const checkUserHasApp = async (userId) => {
    try {
      const appsQuery = query(
        collection(db, 'apps'),
        where('userId', '==', userId),
        limit(1)
      );
      const appsSnapshot = await getDocs(appsQuery);
      return !appsSnapshot.empty;
    } catch (error) {
      console.error('Erro ao verificar aplicativo:', error);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const user = userCredential.user;

      // Decidir destino de forma atômica para evitar qualquer flicker
      const [isAdmin, hasApp] = await Promise.all([
        checkUserIsAdmin(user.uid),
        checkUserHasApp(user.uid)
      ]);

      const target = isAdmin ? '/admin' : hasApp ? '/dashboard' : '/app-creation';
      navigate(target, { replace: true });

    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Verificar se usuário existe no Firestore
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        // Criar documento básico se não existir
        await setDoc(userDocRef, {
          fullName: user.displayName,
          email: user.email,
          profileImage: user.photoURL,
          uid: user.uid,
          createdAt: new Date(),
          updatedAt: new Date(),
          isNewUser: true,
          userStatus: 'pending',
          loginProvider: 'google',
          // Campos opcionais vazios para evitar erros se forem esperados
          phone: '',
          cpf: '',
          category: ''
        });
      }

      // Decidir destino
      const [isAdmin, hasApp] = await Promise.all([
        checkUserIsAdmin(user.uid),
        checkUserHasApp(user.uid)
      ]);

      const target = isAdmin ? '/admin' : hasApp ? '/dashboard' : '/app-creation';
      navigate(target, { replace: true });

    } catch (error) {
      console.error("Erro no login com Google:", error);
      if (error.code === 'auth/popup-closed-by-user') {
        setError('O login foi cancelado.');
      } else {
        setError('Erro ao fazer login com Google. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetEmail) {
      setError('Por favor, informe seu email');
      return;
    }

    // Validação de formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(resetEmail)) {
      setError('Por favor, informe um email válido');
      return;
    }

    setResetLoading(true);
    setError('');

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetSuccess(true);
      setError('Email de recuperação enviado! Verifique sua caixa de entrada.');
    } catch (error) {
      // Mensagens de erro mais amigáveis
      if (error.code === 'auth/user-not-found') {
        setError('Não encontramos uma conta com este email');
      } else if (error.code === 'auth/invalid-email') {
        setError('O endereço de email fornecido não é válido');
      } else {
        setError(error.message);
      }
    } finally {
      setResetLoading(false);
    }
  };

  // Função para alternar entre mostrar e ocultar a senha
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-[#fafafa]">
      <div className="max-w-md w-full relative z-10">

        {/* Header Ultra Minimalista */}
        <div className="text-center mb-10">
          <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
            {showResetPassword ? 'Recuperar Senha' : 'Acesse sua conta'}
          </h2>
          <p className="mt-2 text-sm text-gray-500 font-light">
            {!showResetPassword ? 'Bem-vindo de volta ao painel' : 'Enviaremos instruções para o seu e-mail'}
          </p>
        </div>

        <div className="bg-white py-10 px-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-2xl border border-gray-100">
          {error && (
            <div className={`mb-8 p-4 rounded-xl text-sm font-medium ${error.includes('enviado') ? 'bg-green-50 border border-green-100 text-green-700' : 'bg-red-50 border border-red-100 text-red-600'}`}>
              {error}
            </div>
          )}

          {!showResetPassword ? (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="block w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 focus:bg-white focus:ring-1 focus:ring-black focus:border-black placeholder-gray-400 transition duration-300 outline-none"
                    placeholder="seu@email.com"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">Senha</label>
                    <button
                      type="button"
                      onClick={() => setShowResetPassword(true)}
                      className="text-xs font-medium text-gray-500 hover:text-black transition-colors"
                    >
                      Esqueceu a senha?
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="block w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 focus:bg-white focus:ring-1 focus:ring-black focus:border-black placeholder-gray-400 transition duration-300 outline-none pr-12"
                      placeholder="••••••••"
                    />
                    <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                      <button
                        type="button"
                        className="text-gray-400 hover:text-gray-700 focus:outline-none transition-colors"
                        onClick={togglePasswordVisibility}
                      >
                        {showPassword ? (
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-3.5 px-4 rounded-xl shadow-sm text-sm font-medium text-white bg-[#0a0a0a] hover:bg-[#1a1a1a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : 'Entrar na plataforma'}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-6">
              {!resetSuccess ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email cadastrado</label>
                    <input
                      type="email"
                      required
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="block w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 focus:bg-white focus:ring-1 focus:ring-black focus:border-black placeholder-gray-400 transition duration-300 outline-none"
                      placeholder="seu@email.com"
                    />
                  </div>

                  <div className="flex flex-col space-y-3 pt-2">
                    <button
                      onClick={handleResetPassword}
                      disabled={resetLoading}
                      className="w-full flex justify-center py-3.5 px-4 rounded-xl shadow-sm text-sm font-medium text-white bg-[#0a0a0a] hover:bg-[#1a1a1a] focus:outline-none transition duration-300 disabled:opacity-50"
                    >
                      {resetLoading ? 'Enviando...' : 'Enviar email de recuperação'}
                    </button>
                    <button
                      onClick={() => {
                        setShowResetPassword(false);
                        setResetEmail('');
                        setError('');
                      }}
                      className="w-full flex justify-center py-3.5 px-4 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none transition duration-300"
                    >
                      Cancelar
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-4">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-50 mb-4">
                    <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900">Email enviado</h3>
                  <p className="mt-2 text-sm text-gray-500 font-light">
                    As instruções foram enviadas para <strong>{resetEmail}</strong>.
                  </p>
                  <div className="mt-8">
                    <button
                      onClick={() => {
                        setShowResetPassword(false);
                        setResetSuccess(false);
                        setResetEmail('');
                        setError('');
                      }}
                      className="w-full flex justify-center py-3.5 px-4 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition duration-300"
                    >
                      Voltar para o login
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {!showResetPassword && (
            <>
              <div className="mt-8">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-100" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-400 font-light">
                      ou continue com
                    </span>
                  </div>
                </div>

                <div className="mt-8">
                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    disabled={loading}
                    className="w-full flex justify-center items-center py-3.5 px-4 border border-gray-200 rounded-xl shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 focus:outline-none transition duration-300 disabled:opacity-50"
                  >
                    <FcGoogle className="h-5 w-5 mr-3" />
                    Entrar com Google
                  </button>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-50 text-center">
                <p className="text-sm text-gray-500">
                  Ainda não tem conta? {' '}
                  <Link
                    to="/register"
                    className="font-semibold text-black hover:underline transition-all"
                  >
                    Criar uma conta
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>

        {/* Back to Home Link */}
        <div className="mt-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center text-sm font-medium text-gray-400 hover:text-gray-900 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Voltar para a página inicial
          </Link>
        </div>

      </div>
    </div>
  );
}

export default Login;